import { inject, Injectable, OnDestroy, untracked } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import {
  AccessEvalResult,
  AccessEvalStatus,
  EdgeEnrollProgressPayload,
  EdgeNetInfo,
  EMBEDDED_ENROLL_STAGE_PERCENT,
  GatePassage,
  HubConnectionStatus,
  IEdgeEventPayload,
} from '../entities/access-edges.interface';
import { LocalStorageFacade } from '@application/localstorage';
import { DateTimeService } from '@application/data-access';
import { DateTime } from 'luxon';
import {
  AemEventPayload,
  mapAemEventToEdgeEventLog,
  toEdgeBoolean,
  toEdgeNumber,
} from './aem-event.util';

interface EdgeOnlinePayload {
  edgeId?: string;
  apiKeyKind?: 0 | 1;
}

interface EdgeOfflinePayload {
  edgeId?: string;
}

interface HeartbeatPayload {
  edgeId?: string;
  signalQuality?: ESignalQuality;
  accessPointName?: string;
}

interface NetworkInfoPayload {
  edgeId?: string;
  edge?: string;
  id?: string;
  info?: EdgeNetInfo;
  net?: string | EdgeNetInfo;
}

interface GatePassagePayload {
  edgeId?: string;
  logicalDirection?: 0 | 1 | 2 | 3 | 'None' | 'Entry' | 'Exit' | 'Both';
  physicalDirection?: 0 | 1 | 'SideAtoB' | 'SideBtoA';
  passageCompleted?: boolean;
  isTriggeredByRelease?: boolean;
  releaseRequestId?: string;
  ts?: string | number;
  payload?: {
    logicalDirection?: 0 | 1 | 2 | 3 | 'None' | 'Entry' | 'Exit' | 'Both';
    physicalDirection?: 0 | 1 | 'SideAtoB' | 'SideBtoA';
    releaseRequestId?: string | null;
    isTriggeredByRelease?: boolean;
    passageCompleted?: boolean;
    payloadType?: number;
    version?: string | null;
    requestId?: string;
  };
}

interface AccessEvalResultPayload {
  edgeId?: string;
  status?: string;
  isAllowed?: boolean;
  logicalDirection?: string;
  personId?: string;
  name?: string | null;
  message?: string | null;
  effectiveUntilUtc?: string;
  logs?: any;
  error?: string | null;
  requestId?: string;
  ts?: string;
  accessPoint?: string;
  payload?: { accessPoint?: string };
}

/** Payload for SignalR EdgeEventLog (target "EdgeEventLog"). Backend may send top-level fields or nested under entry. */
interface EdgeEventLogPayload {
  tenantId?: string;
  edgeId?: string;
  type?: number;
  level?: number;
  occurredOnUtc?: string;
  personId?: string | null;
  allowed?: boolean;
  code?: string;
  message?: string;
  correlationId?: string | null;
  payloadJson?: string;
  /** When backend sends full event shape (e.g. { id, entry, payload }), use entry for code/payloadJson. */
  entry?: {
    Code?: string;
    PayloadJson?: string;
    EdgeId?: string;
    TenantId?: string;
    Type?: number;
    Level?: number;
    OccurredOnUtc?: string;
    PersonId?: string | null;
    Allowed?: boolean;
    Message?: string;
    CorrelationId?: string | null;
  };
}
class AggressiveReconnectPolicy implements signalR.IRetryPolicy {
  private readonly maxDelayMs = 60000;
  private readonly baseDelayMs = 500;

  public nextRetryDelayInMilliseconds(
    retryContext: signalR.RetryContext
  ): number {
    if (retryContext.previousRetryCount === 0) {
      return 0;
    }

    const attempt = retryContext.previousRetryCount - 1;
    const delay = this.baseDelayMs * Math.pow(2, attempt);

    return Math.min(delay, this.maxDelayMs);
  }
}

export interface AccessPointEdgeOnlineEvent {
  edgeId: string;
  apiKeyKind?: 0 | 1;
}

export interface AccessPointEdgeOfflineEvent {
  edgeId: string;
}

export enum ESignalQuality {
  Excellent = 4,
  Good = 3,
  Fair = 2,
  Poor = 1,
  Offline = 0,
}

export const signalQualityDescriptions = {
  [ESignalQuality.Excellent]: 'LIVE_ACCESS_MONITOR.ACTIVITY.CONNECTION_QUALITY_EXCELLENT',
  [ESignalQuality.Good]: 'LIVE_ACCESS_MONITOR.ACTIVITY.CONNECTION_QUALITY_GOOD',
  [ESignalQuality.Fair]: 'LIVE_ACCESS_MONITOR.ACTIVITY.CONNECTION_QUALITY_FAIR',
  [ESignalQuality.Poor]: 'LIVE_ACCESS_MONITOR.ACTIVITY.CONNECTION_QUALITY_POOR',
  [ESignalQuality.Offline]: 'LIVE_ACCESS_MONITOR.ACTIVITY.CONNECTION_QUALITY_OFFLINE',
  ['Excellent']: 'LIVE_ACCESS_MONITOR.ACTIVITY.CONNECTION_QUALITY_EXCELLENT',
  ['Good']: 'LIVE_ACCESS_MONITOR.ACTIVITY.CONNECTION_QUALITY_GOOD',
  ['Fair']: 'LIVE_ACCESS_MONITOR.ACTIVITY.CONNECTION_QUALITY_FAIR',
  ['Poor']: 'LIVE_ACCESS_MONITOR.ACTIVITY.CONNECTION_QUALITY_POOR',
  ['Offline']: 'LIVE_ACCESS_MONITOR.ACTIVITY.CONNECTION_QUALITY_OFFLINE',
}

export interface AccessPointEdgeHeartbeatEvent {
  edgeId: string;
  signalQuality: ESignalQuality | null;
  accessPointName?: string;
}

export interface AccessPointEdgePresenceSnapshotEvent {
  items: {
    edgeId: string;
    online: boolean;
    signalQuality: ESignalQuality | null;
  }[];
  ts: string;
}

export interface AccessPointEdgeNetworkInfoEvent {
  edgeId: string;
  net?: EdgeNetInfo | null;
  source: 'networkInfo' | 'netInfo';
}

export interface AccessPointEdgeGatePassageEvent {
  edgeId: string;
  lastGatePassage: GatePassage;
}

export interface AccessPointEdgeAccessEvalResultEvent {
  edgeId: string;
  accessEvalResult: AccessEvalResult;
}

export interface AccessPointEdgeEventLogEvent {
  edgeId: string;
  tenantId: string;
  type: number;
  level: number;
  occurredOnUtc: string;
  personId: string | null;
  allowed: boolean;
  code: string;
  message: string;
  correlationId: string | null;
  payloadJson: string;
}

/**
 * O carimbo de tempo do edge é um instante UTC com um nome que não termina em
 * `Utc` — declarado aqui, entra na conversão como qualquer outro.
 */
const EDGE_UTC_DATE_FIELDS = ['ts'] as const;

@Injectable({ providedIn: 'root' })
export class AccessPointEdgeHubService implements OnDestroy {
  private readonly appConfig = inject(APP_CONFIG);
  private readonly apiBase = this.appConfig.apiUrl.access_edge_manager_service;
  private readonly localStorageService = inject(LocalStorageFacade);
  private readonly dateTimeService = inject(DateTimeService);

  private connection: HubConnection | null = null;
  private healthCheckInterval: any = null;
  private isStarting = false;
  private lastGatePassagePayloadJson: string | null = null;

  private readonly hubStatusSubject = new BehaviorSubject<HubConnectionStatus>(
    'disconnected'
  );
  public readonly hubStatus$: Observable<HubConnectionStatus> =
    this.hubStatusSubject.asObservable();

  private readonly edgeOnlineSubject =
    new Subject<AccessPointEdgeOnlineEvent>();
  public readonly edgeOnline$ = this.edgeOnlineSubject.asObservable();

  private readonly edgeOfflineSubject =
    new Subject<AccessPointEdgeOfflineEvent>();
  public readonly edgeOffline$ = this.edgeOfflineSubject.asObservable();

  private readonly heartbeatSubject =
    new Subject<AccessPointEdgeHeartbeatEvent>();
  public readonly heartbeat$ = this.heartbeatSubject.asObservable();

  private readonly presenceSnapshotSubject =
    new Subject<AccessPointEdgePresenceSnapshotEvent>();
  public readonly presenceSnapshot$ = this.presenceSnapshotSubject.asObservable();

  private readonly networkInfoSubject =
    new Subject<AccessPointEdgeNetworkInfoEvent>();
  public readonly networkInfo$ = this.networkInfoSubject.asObservable();

  private readonly gatePassageSubject =
    new Subject<AccessPointEdgeGatePassageEvent>();
  public readonly gatePassage$ = this.gatePassageSubject.asObservable();

  private readonly accessEvalResultSubject =
    new Subject<AccessPointEdgeAccessEvalResultEvent>();
  public readonly accessEvalResult$ =
    this.accessEvalResultSubject.asObservable();

  private readonly edgeEventLogSubject =
    new Subject<AccessPointEdgeEventLogEvent>();
  public readonly edgeEventLog$ = this.edgeEventLogSubject.asObservable();

  private readonly enrollProgressSubject =
    new Subject<EdgeEnrollProgressPayload>();
  /**
   * Progresso do cadastro de digital embarcada.
   *
   * Emite tudo o que chega no evento `progress`; **filtrar por `corrId` é
   * responsabilidade de quem consome**, porque o hub não sabe qual dos cadastros
   * em curso pertence a esta aba.
   */
  public readonly enrollProgress$ = this.enrollProgressSubject.asObservable();

  /**
   * Grupos de equipamento assinados nesta conexão.
   *
   * Existe porque a membership de grupo **não sobrevive a uma reconexão** — o
   * servidor reconstrói a conexão do zero e esquece os grupos. Sem re-assinar no
   * `onreconnected`, o progresso morre calado a meio de um cadastro.
   */
  private readonly subscribedEdges = new Set<string>();

  public async start(): Promise<void> {
    if (
      this.isStarting ||
      this.connection?.state === HubConnectionState.Connected
    ) {
      return;
    }

    if (this.connection?.state === HubConnectionState.Connecting) {
      return;
    }

    this.isStarting = true;
    this.emitStatus('connecting');

    try {
      if (this.connection) {
        await this.stop();
      }

      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(`${this.apiBase}/hubs/edge-realtime`, {
          withCredentials: true,
          transport: HttpTransportType.WebSockets,
          skipNegotiation: true,
          // Lido a CADA chamada, e não uma vez capturado no closure: a conexão é
          // longeva e o cadastro de digital embarcada dura até 2 minutos, portanto
          // uma reconexão pode cair depois de um refresh de token. Com o valor
          // preso no closure ela reautenticava com o token velho.
          accessTokenFactory: () => this.readToken(),
        })
        .configureLogging(LogLevel.Information)
        .withAutomaticReconnect(new AggressiveReconnectPolicy())
        .build();

      this.setupHandlers();

      await this.connection.start();
      this.emitStatus('connected');
      // Cobre o caso de alguém ter pedido a assinatura com a conexão ainda em pé
      // de ser: o pedido ficou no Set e é honrado assim que há ligação.
      await this.resubscribeEdges();

    } catch (error) {
      this.emitStatus('disconnected');
      throw error;
    } finally {
      this.isStarting = false;
    }
  }

  public async stop(): Promise<void> {
    this.emitStatus('disconnected');
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }

    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (error) {}
      this.connection = null;
      this.lastGatePassagePayloadJson = null;
    }
  }

  public isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  private setupHandlers(): void {
    if (!this.connection) return;

    this.connection.off('edgeOnline');
    this.connection.off('edgeOffline');
    this.connection.off('heartbeat');
    this.connection.off('presenceSnapshot');
    this.connection.off('networkInfo');
    this.connection.off('netInfo');
    this.connection.off('ack');
    this.connection.off('progress');
    this.connection.off('gatePassage');
    this.connection.off('accessEvalResult');
    this.connection.off('EdgeEventLog');

    this.connection.on('edgeOnline', (p: EdgeOnlinePayload | string) => {
      const edgeId = typeof p === 'string' ? p : p?.edgeId ?? '';
      const apiKeyKind = typeof p === 'object' ? p?.apiKeyKind : undefined;
      if (edgeId) {
        this.edgeOnlineSubject.next({ edgeId, apiKeyKind });
      }
    });

    this.connection.on('edgeOffline', (p: EdgeOfflinePayload | string) => {
      const edgeId = typeof p === 'string' ? p : p?.edgeId ?? '';
      if (edgeId) {
        this.edgeOfflineSubject.next({ edgeId });
      }
    });

    this.connection.on('heartbeat', (p: HeartbeatPayload | string) => {
      this.syncStatusFromConnection();
      const edgeId = typeof p === 'string' ? p : p?.edgeId ?? '(?)';
      if (edgeId) {
        this.heartbeatSubject.next({
          edgeId,
          signalQuality: typeof p === 'string' ? null : p?.signalQuality,
          accessPointName: typeof p === 'string' ? null : p?.accessPointName,
        });
      }
    });
  
    this.connection.on('presenceSnapshot', (p: any) => {
      this.presenceSnapshotSubject.next(this.dateTimeService.toLocalObject(p));
    });

    this.connection.on('networkInfo', (evt: NetworkInfoPayload) => {
      this.handleNetworkInfo(this.dateTimeService.toLocalObject(evt), 'networkInfo');
    });

    this.connection.on('netInfo', (evt: NetworkInfoPayload) => {
      this.handleNetworkInfo(evt, 'netInfo');
    });

    this.connection.on('ack', () => {
      // no-op
    });


    this.connection.on('progress', (evt: EdgeEnrollProgressPayload) => {
      if (!evt) return;
      // O `percent` vem do servidor, mas a etapa é o dado fiável: se um evento
      // chegar sem ele, derivamos da tabela em vez de deixar a barra parada.
      const percent =
        typeof evt.percent === 'number'
          ? evt.percent
          : evt.stage
          ? EMBEDDED_ENROLL_STAGE_PERCENT[evt.stage]
          : undefined;
      this.enrollProgressSubject.next({ ...evt, percent });
    });
    
    // Acessos decididos no equipamento (handoff de 09/10/2026). O backend pode ou
    // não publicá-los aqui; quando publica, entram no monitor pelo mesmo caminho
    // do `EdgeEventLog`. `ts` é a hora do acesso, em UTC — converte como os outros.
    this.connection.on('aem:event', (evt: AemEventPayload | AemEventPayload[]) => {
      this.syncStatusFromConnection();
      const payload = Array.isArray(evt) ? evt[0] : evt;
      if (!payload) return;
      const eventLog = mapAemEventToEdgeEventLog(
        this.toLocalPayload(payload),
        this.nowInUserZone()
      );
      if (eventLog) this.edgeEventLogSubject.next(eventLog);
    });

    this.connection.on('gatePassage', (evt: GatePassagePayload) => {
      this.syncStatusFromConnection();
      const payloadJson = JSON.stringify(evt);
      if (this.lastGatePassagePayloadJson === payloadJson) return;
      this.lastGatePassagePayloadJson = payloadJson;
      this.handleGatePassage(this.toLocalPayload(evt));
    });

    this.connection.on('accessEvalResult', (evt: AccessEvalResultPayload) => {
      this.handleAccessEvalResult(this.toLocalPayload(evt));
    });

    this.connection.on('EdgeEventLog', (evt: EdgeEventLogPayload | EdgeEventLogPayload[]) => {
      this.syncStatusFromConnection();
      const payload = Array.isArray(evt) ? evt[0] : evt;
      if (payload) this.handleEdgeEventLog(this.dateTimeService.toLocalObject(payload));
    });

    this.connection.onclose(async (error) => {
      this.emitStatus('disconnected');
      if (error) {
      } else {
      }

      setTimeout(async () => {
        if (this.connection?.state === HubConnectionState.Disconnected) {
          try {
            await this.start();
          } catch (err) {}
        }
      }, 60000);
    });

    this.connection.onreconnecting((error) => {
      this.emitStatus('reconnecting');
      if (error) {
      }
    });

    this.connection.onreconnected(async (connectionId) => {
      this.emitStatus('connected');
      // O grupo não sobrevive à reconexão — sem isto, um cadastro em curso deixa
      // de receber progresso e o utilizador vê a barra congelada em "preparando
      // o sensor" até o POST responder.
      await this.resubscribeEdges();
    });
  }

  /**
   * Passa a receber o progresso dos cadastros deste equipamento.
   *
   * **A ordem importa:** assinar ANTES de disparar o POST de cadastro. A
   * preparação do sensor começa de imediato e as primeiras notificações
   * perdem-se se a assinatura vier depois.
   *
   * Não lança: se a conexão não estiver de pé, guarda o id e o
   * `onreconnected`/`start` seguinte trata de assinar. Progresso é melhor
   * esforço — a autoridade é a resposta do POST.
   */
  public async subscribeEdge(edgeId: string): Promise<void> {
    if (!edgeId) return;
    this.subscribedEdges.add(edgeId);
    if (this.connection?.state !== HubConnectionState.Connected) return;
    try {
      await this.connection.invoke('SubscribeEdge', edgeId);
    } catch (error) {}
  }

  /**
   * Deixa de receber o progresso deste equipamento.
   *
   * É isto que o modal chama ao fechar — **nunca `stop()`**. A conexão é
   * singleton de raiz e é partilhada com a tela de configurações e com a navbar;
   * pará-la aqui apagava o monitor de acesso ao vivo do resto da aplicação.
   */
  public async unsubscribeEdge(edgeId: string): Promise<void> {
    if (!edgeId) return;
    this.subscribedEdges.delete(edgeId);
    if (this.connection?.state !== HubConnectionState.Connected) return;
    try {
      await this.connection.invoke('UnsubscribeEdge', edgeId);
    } catch (error) {}
  }

  private async resubscribeEdges(): Promise<void> {
    for (const edgeId of this.subscribedEdges) {
      try {
        await this.connection?.invoke('SubscribeEdge', edgeId);
      } catch (error) {}
    }
  }

  private readToken(): string {
    try {
      const raw = this.localStorageService.getItem('authorization');
      return raw ? JSON.parse(raw)?.Token || '' : '';
    } catch (error) {
      return '';
    }
  }

  /**
   * Traz um payload do hub para a hora de parede da conta.
   *
   * Esta é a fronteira: o SignalR não passa por interceptor nenhum, portanto é
   * aqui que a conversão tem de acontecer. Daí para dentro tudo circula num fuso
   * só, e nem store, nem componente, nem pipe precisa de saber que o valor veio
   * em UTC.
   *
   * O `ts` vai declarado como exceção porque o nome não termina em `Utc` e sem
   * isso passaria intacto, ficando adiantado pelo offset do tenant — é o que
   * fazia as janelas de frescura (`STATUS_TIMEOUT` e a de 60s no `edge-control`)
   * nunca dispararem, mostrando passagens paradas como recentes.
   *
   * Só `gatePassage` e `accessEvalResult` carregam `ts` consumido a jusante; o
   * `EdgeEventLog` traz `occurredOnUtc`, que a regra do sufixo já apanha.
   */
  private toLocalPayload<T extends { ts?: string | number }>(evt: T): T {
    return this.dateTimeService.toLocalObject(evt, EDGE_UTC_DATE_FIELDS);
  }

  private handleNetworkInfo(
    evt: NetworkInfoPayload | string,
    source: 'networkInfo' | 'netInfo'
  ): void {
    const edgeId =
      typeof evt === 'string' ? evt : evt?.edgeId ?? evt?.edge ?? evt?.id ?? '';

    if (!edgeId) return;

    const netObj = this.coerceNet(evt);
    this.networkInfoSubject.next({ edgeId, net: netObj, source });
  }

  private coerceNet(evt: NetworkInfoPayload | string): EdgeNetInfo | null {
    if (!evt) return null;

    if (typeof evt === 'string') {
      try {
        return JSON.parse(evt) as EdgeNetInfo;
      } catch {
        return null;
      }
    }

    if (evt.info && typeof evt.info === 'object') {
      return evt.info as EdgeNetInfo;
    }

    if (typeof evt.net === 'string') {
      try {
        return JSON.parse(evt.net) as EdgeNetInfo;
      } catch {
        return null;
      }
    }

    if (evt.net && typeof evt.net === 'object') {
      return evt.net as EdgeNetInfo;
    }

    return null;
  }

  private handleGatePassage(evt: GatePassagePayload): void {
    const edgeId = evt?.edgeId;
    if (!edgeId) return;

    const logicalDirection =
      evt.payload?.logicalDirection ?? evt.logicalDirection;
    const physicalDirection =
      evt.payload?.physicalDirection ?? evt.physicalDirection;
    const passageCompleted =
      evt.payload?.passageCompleted ?? evt.passageCompleted;
    const isTriggeredByRelease =
      evt.payload?.isTriggeredByRelease ?? evt.isTriggeredByRelease;
    const releaseRequestId =
      evt.payload?.releaseRequestId ?? evt.releaseRequestId;
    const ts = evt.ts ?? DateTime.now().toISO();

    let normalizedLogicalDirection: number = 0;
    if (typeof logicalDirection === 'number') {
      normalizedLogicalDirection = logicalDirection;
    } else if (logicalDirection === 'Entry') {
      normalizedLogicalDirection = 1;
    } else if (logicalDirection === 'Exit') {
      normalizedLogicalDirection = 2;
    } else if (logicalDirection === 'Both') {
      normalizedLogicalDirection = 3;
    }

    let normalizedPhysicalDirection: number = 0;
    if (typeof physicalDirection === 'number') {
      normalizedPhysicalDirection = physicalDirection;
    } else if (physicalDirection === 'SideBtoA') {
      normalizedPhysicalDirection = 1;
    }

    const lastGatePassage: GatePassage = {
      edgeId,
      logicalDirection: normalizedLogicalDirection,
      physicalDirection: normalizedPhysicalDirection,
      passageCompleted: passageCompleted ?? false,
      isTriggeredByRelease: isTriggeredByRelease ?? false,
      releaseRequestId: releaseRequestId ?? null,
      ts,
      payload: {
        logicalDirection: normalizedLogicalDirection,
        physicalDirection: normalizedPhysicalDirection,
        releaseRequestId: releaseRequestId ?? null,
        isTriggeredByRelease: isTriggeredByRelease ?? false,
        passageCompleted: passageCompleted ?? false,
        payloadType: evt.payload?.payloadType ?? 0,
        version: evt.payload?.version ?? null,
        requestId: evt.payload?.requestId ?? '',
      },
    };

    this.gatePassageSubject.next({ edgeId, lastGatePassage });
  }

  private handleAccessEvalResult(evt: AccessEvalResultPayload): void {
    const edgeId = evt?.edgeId;
    if (!edgeId) return;

    const accessPoint =
      evt.accessPoint ?? evt.payload?.accessPoint ?? '';
    const accessEvalResult: AccessEvalResult = {
      edgeId,
      status: (evt.status as AccessEvalStatus) ?? AccessEvalStatus.Error,
      isAllowed: evt.isAllowed ?? false,
      logicalDirection: evt.logicalDirection ?? '',
      personId: evt.personId ?? '',
      name: evt.name ?? null,
      message: evt.message ?? null,
      effectiveUntilUtc: evt.effectiveUntilUtc ?? '',
      logs: evt.logs ?? null,
      error: evt.error ?? null,
      requestId: evt.requestId ?? '',
      ts: evt.ts ?? DateTime.now().toISO(),
      accessPoint: accessPoint || undefined,
    };

    this.accessEvalResultSubject.next({ edgeId, accessEvalResult });
  }

  private nowInUserZone(): string {
    return DateTime.now().toISO();
  }

  private handleEdgeEventLog(evt: EdgeEventLogPayload): void {
    const entry = evt?.entry;
    const edgeId = (entry?.EdgeId ?? evt?.edgeId) ?? '';
    if (!edgeId) return;

    const eventLog: AccessPointEdgeEventLogEvent = {
      edgeId,
      tenantId: (entry?.TenantId ?? evt.tenantId) ?? '',
      // O stream grava `Type`, `Level` e `Allowed` como texto (`"0"`); um `"0"`
      // cru seria verdadeiro a jusante e mostraria como liberado um acesso negado.
      type: toEdgeNumber(entry?.Type ?? evt.type) ?? 0,
      level: toEdgeNumber(entry?.Level ?? evt.level) ?? 0,
      occurredOnUtc:
        (entry?.OccurredOnUtc ?? evt.occurredOnUtc) ?? this.nowInUserZone(),
      personId: entry?.PersonId ?? evt.personId ?? null,
      allowed: toEdgeBoolean(entry?.Allowed ?? evt.allowed) ?? false,
      code: (entry?.Code ?? evt.code) ?? '',
      message: (entry?.Message ?? evt.message) ?? '',
      correlationId: (entry?.CorrelationId ?? evt.correlationId) ?? null,
      payloadJson: (entry?.PayloadJson ?? evt.payloadJson) ?? '',
    };
    this.edgeEventLogSubject.next(eventLog);
  }

  // `hubStatus$` alimenta um `toSignal` no `EdgeControlService`. Os callbacks do
  // SignalR (`onclose`/`onreconnecting`/`onreconnected`) disparam essa escrita;
  // com um consumer reativo vazado ela rebentaria com NG0600 (Sentry 7663881749).
  private emitStatus(status: HubConnectionStatus): void {
    untracked(() => this.hubStatusSubject.next(status));
  }

  /** Keeps hubStatus in sync with actual connection when we receive hub messages (e.g. after reconnect). */
  private syncStatusFromConnection(): void {
    if (
      this.connection?.state === HubConnectionState.Connected &&
      this.hubStatusSubject.getValue() !== 'connected'
    ) {
      this.emitStatus('connected');
    }
  }

  ngOnDestroy(): void {
    this.stop();
    this.healthCheckInterval = null;
    this.hubStatusSubject.complete();
    this.edgeOnlineSubject.complete();
    this.edgeOfflineSubject.complete();
    this.heartbeatSubject.complete();
    this.presenceSnapshotSubject.complete();
    this.networkInfoSubject.complete();
    this.gatePassageSubject.complete();
    this.accessEvalResultSubject.complete();
    this.edgeEventLogSubject.complete();
  }
}
